/**
 * A year-by-year range chart, as arithmetic (FE #94).
 *
 * One bar per year from a low to a high, an optional reference band (the
 * normal), shaded gaps where no year is drawn, labelled marks (the records)
 * and a hit band per year. Positions, ticks and labels only: no element tree,
 * no colours and no locale, so both apps draw the same chart from it and the
 * webapp's plain SVG and mobile's react-native-svg cannot drift apart.
 *
 * Values are in the user's units already; the scale is built from them.
 * The rain chart under it is the same layout with `lo: 0`.
 */

export type YearRangePoint = {year: number; lo: number; hi: number};

export type YearRangeMarkKind = 'max' | 'min' | 'rain';

export type YearRangeMark = {
  kind: YearRangeMarkKind;
  /** The record's year first, then the years that tie it. */
  years: number[];
  value: number;
  /** Already formatted, e.g. "25.0° · 2001 & 2018". */
  label: string;
};

export type YearRangeLayoutOptions = {
  width: number;
  height: number;
  /** First and last year of the axis (inclusive), drawn or not. */
  x0: number;
  x1: number;
  normal?: {min: number; max: number};
  marks?: YearRangeMark[];
  /** Pins the bottom of the value axis, e.g. 0 for rain. */
  yFloor?: number;
  padding?: Partial<YearRangePadding>;
};

export type YearRangePadding = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type YearRangeBar = {
  year: number;
  x: number;
  yHigh: number;
  yLow: number;
};

export type YearRangeGap = {
  from: number;
  to: number;
  x: number;
  width: number;
  /** Centre of the run, for its "no data" label. */
  labelX: number;
  /** Whether the run is wide enough to carry the label. */
  showLabel: boolean;
};

export type YearRangeMarkLabel = {
  kind: YearRangeMarkKind;
  label: string;
  x: number;
  y: number;
  anchor: 'start' | 'end';
  dots: {year: number; x: number; y: number}[];
};

export type YearRangeLayout = {
  plot: {left: number; right: number; top: number; bottom: number};
  /** Pixels per year. */
  slot: number;
  barWidth: number;
  yMin: number;
  yMax: number;
  bars: YearRangeBar[];
  yTicks: {value: number; y: number; label: string}[];
  xTicks: {year: number; x: number}[];
  band?: {yTop: number; yBottom: number};
  gaps: YearRangeGap[];
  markLabels: YearRangeMarkLabel[];
  hitBands: {year: number; x: number; width: number}[];
  xOf: (year: number) => number;
  yOf: (value: number) => number;
};

const DEFAULT_PADDING: YearRangePadding = {
  left: 30,
  right: 10,
  top: 22,
  bottom: 22,
};

/** A finger or a mouse needs this much, whatever the year density. */
export const MIN_HIT_WIDTH = 7;

/** Runs of at least this many undrawn years are shaded as a gap. */
export const MIN_GAP_YEARS = 3;

/** A gap narrower than this keeps its shading but drops its label. */
const MIN_GAP_LABEL_WIDTH = 26;

/** A mark closer than this to the right edge has its label end at the mark. */
const LABEL_ROOM = 150;
const LABEL_OFFSET = 8;
/** Above the dot for a high, below it (by a line) for a low when it fits. */
const LABEL_ABOVE = 7;
const LABEL_BELOW = 14;

const MAX_BAR_WIDTH = 6;

/** Five ticks or so: the smallest 1/2/5 x 10^n step covering a fifth. */
const niceStep = (range: number) => {
  const raw = range / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].find((m) => m * magnitude >= raw - 1e-9)!;
  return step * magnitude;
};

const decimalsOf = (step: number) =>
  step >= 1 ? 0 : Math.ceil(-Math.log10(step) - 1e-9);

const xStepFor = (span: number) => (span > 80 ? 20 : span > 40 ? 10 : 5);

export const yearRangeLayout = (
  series: YearRangePoint[],
  options: YearRangeLayoutOptions,
): YearRangeLayout => {
  const {width, height, x0, x1, normal, marks = [], yFloor} = options;
  const pad = {...DEFAULT_PADDING, ...options.padding};
  const plot = {
    left: pad.left,
    right: width - pad.right,
    top: pad.top,
    bottom: height - pad.bottom,
  };
  const plotWidth = plot.right - plot.left;
  const plotHeight = plot.bottom - plot.top;

  const span = x1 - x0 + 1;
  const slot = plotWidth / span;
  const xOf = (year: number) =>
    plot.left + ((year - x0 + 0.5) / span) * plotWidth;

  const values = [
    ...series.flatMap((p) => [p.lo, p.hi]),
    ...(normal ? [normal.min, normal.max] : []),
    ...marks.map((m) => m.value),
  ];
  const dataMin = yFloor ?? Math.min(...values);
  const dataMax = Math.max(...values, dataMin);
  const step = niceStep(Math.max(dataMax - dataMin, 1e-6));
  const yMin = yFloor ?? Math.floor(dataMin / step) * step;
  let yMax = Math.ceil(dataMax / step) * step;
  if (yMax <= yMin) {
    yMax = yMin + step;
  }
  const yOf = (value: number) =>
    plot.top + ((yMax - value) / (yMax - yMin)) * plotHeight;

  const decimals = decimalsOf(step);
  const yTicks: YearRangeLayout['yTicks'] = [];
  for (let i = 0; yMin + i * step <= yMax + step / 1000; i += 1) {
    const value = +(yMin + i * step).toFixed(decimals);
    yTicks.push({value, y: yOf(value), label: value.toFixed(decimals)});
  }

  const xStep = xStepFor(span);
  const xTicks: YearRangeLayout['xTicks'] = [];
  for (let year = Math.ceil(x0 / xStep) * xStep; year <= x1; year += xStep) {
    xTicks.push({year, x: xOf(year)});
  }

  const sorted = [...series].sort((a, b) => a.year - b.year);
  const bars = sorted.map((p) => ({
    year: p.year,
    x: xOf(p.year),
    yHigh: yOf(p.hi),
    yLow: yOf(p.lo),
  }));

  const drawn = new Set(sorted.map((p) => p.year));
  const gaps: YearRangeGap[] = [];
  let runStart: number | undefined;
  for (let year = x0; year <= x1 + 1; year += 1) {
    const missing = year <= x1 && !drawn.has(year);
    if (missing && runStart === undefined) {
      runStart = year;
    } else if (!missing && runStart !== undefined) {
      const to = year - 1;
      if (to - runStart + 1 >= MIN_GAP_YEARS) {
        const x = xOf(runStart) - slot / 2;
        const gapWidth = (to - runStart + 1) * slot;
        gaps.push({
          from: runStart,
          to,
          x,
          width: gapWidth,
          labelX: x + gapWidth / 2,
          showLabel: gapWidth > MIN_GAP_LABEL_WIDTH,
        });
      }
      runStart = undefined;
    }
  }

  const markLabels = marks
    .filter((m) => m.years.length > 0)
    .map((m): YearRangeMarkLabel => {
      const anchorX = xOf(m.years[0]);
      const anchor = anchorX > plot.right - LABEL_ROOM ? 'end' : 'start';
      // A low reads below its dot, clear of the bars above it, unless that
      // would print over the year axis: then above, like a high.
      const below =
        m.kind === 'min' && yOf(m.value) + LABEL_BELOW <= plot.bottom;
      return {
        kind: m.kind,
        label: m.label,
        x: anchor === 'end' ? anchorX - LABEL_OFFSET : anchorX + LABEL_OFFSET,
        y: below ? yOf(m.value) + LABEL_BELOW : yOf(m.value) - LABEL_ABOVE,
        anchor,
        dots: m.years.map((year) => ({year, x: xOf(year), y: yOf(m.value)})),
      };
    });

  const hitWidth = Math.max(slot, MIN_HIT_WIDTH);
  const hitBands = bars.map((bar) => ({
    year: bar.year,
    x: bar.x - hitWidth / 2,
    width: hitWidth,
  }));

  return {
    plot,
    slot,
    barWidth: Math.max(1, Math.min(slot * 0.65, MAX_BAR_WIDTH)),
    yMin,
    yMax,
    bars,
    yTicks,
    xTicks,
    band: normal
      ? {yTop: yOf(normal.max), yBottom: yOf(normal.min)}
      : undefined,
    gaps,
    markLabels,
    hitBands,
    xOf,
    yOf,
  };
};

/** The drawn year nearest a pointer at `x`, or undefined with none drawn. */
export const nearestYear = (layout: YearRangeLayout, x: number) => {
  let best: YearRangeBar | undefined;
  layout.bars.forEach((bar) => {
    if (!best || Math.abs(bar.x - x) < Math.abs(best.x - x)) {
      best = bar;
    }
  });
  return best?.year;
};

/**
 * The drawn year `direction` steps from `year` (arrow keys, swipe actions).
 * Clamps at either end; with nothing selected, starts from the newest year.
 */
export const stepYear = (
  layout: YearRangeLayout,
  year: number | undefined,
  direction: 1 | -1,
) => {
  const years = layout.bars.map((bar) => bar.year);
  if (years.length === 0) {
    return undefined;
  }
  const index = year === undefined ? -1 : years.indexOf(year);
  if (index === -1) {
    return years[years.length - 1];
  }
  return years[Math.min(years.length - 1, Math.max(0, index + direction))];
};

/**
 * One scale shared by every row of the short list (under 10 years), so the
 * rows' mini bars and the normal compare at a glance.
 */
export const miniBarScale = (
  series: YearRangePoint[],
  normal?: {min: number; max: number},
) => {
  const values = [
    ...series.flatMap((p) => [p.lo, p.hi]),
    ...(normal ? [normal.min, normal.max] : []),
  ];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max > min ? max - min : 1;
  return {
    min,
    max,
    fraction: (value: number) => (value - min) / range,
  };
};
